import { useEffect, useRef } from "react";
import { KioskScreensaver } from "@/components/system";
import { useIdle } from "@/hooks/useIdle";
import { useWakeLock } from "@/hooks/useWakeLock";
import { useCameraMotion } from "@/hooks/useCameraMotion";
import type { HomeySnapshot, KioskSettings } from "@/types/homey";

export interface KioskControllerProps {
  /** Normalized kiosk preferences (run through normalizeKioskSettings by the caller). */
  settings: KioskSettings;
  /** Live Homey snapshot (patched in place by useHomey via the websocket). */
  snapshot: HomeySnapshot | undefined;
  /**
   * Whether the dashboard is currently in fullscreen. Drives the screen Wake
   * Lock so the display stays on while mounted as a wall kiosk. The fullscreen
   * toggle itself lives in DashboardContent (the layout owner) — this
   * controller only owns the kiosk *behavior* (idle, screensaver, wake sources,
   * wake lock), not the header chrome.
   */
  isFullscreen: boolean;
}

/**
 * Capability ids that, when transitioning to a truthy value, should wake the
 * screensaver. Covers motion, contact, and presence-style sensors.
 */
function isWakeCapability(capId: string): boolean {
  return capId.startsWith("alarm_") || capId.includes("presence");
}

/**
 * KioskController — composes the kiosk hooks into the dashboard runtime.
 *
 * Responsibilities:
 * - `useIdle` (gated by `settings.enabled`) decides when the screensaver shows.
 *   Touch / pointer / keyboard activity wakes automatically via the hook's own
 *   window listeners.
 * - Renders `KioskScreensaver` (style from settings); its `onWake` calls
 *   `poke()` so the first touch on the overlay dismisses it.
 * - Homey-sensor wake: watches the snapshot device whose id matches
 *   `settings.wakeSensorDeviceId`. When any of its alarm_ / presence capability
 *   values transitions from non-true to true, `poke()` is fired. Previous
 *   values are tracked in a ref so only the rising edge wakes.
 * - Camera wake: `useCameraMotion` runs only while enabled in settings AND the
 *   screensaver is showing (idle) — battery/privacy friendly; it no-ops without
 *   camera permission.
 * - `useWakeLock(isFullscreen)` keeps the screen awake while in kiosk fullscreen.
 *
 * Renders only the screensaver overlay (fixed, z-[60]); no layout chrome.
 *
 * To test: set a short idle timeout in Settings, leave the dashboard idle and
 * confirm the overlay appears, then tap it to dismiss. To exercise the
 * Homey-sensor wake without a real motion event, temporarily point
 * wakeSensorDeviceId at a controllable device's onoff and flip it.
 */
export function KioskController({ settings, snapshot, isFullscreen }: KioskControllerProps) {
  const { isIdle, poke } = useIdle({
    timeoutSec: settings.idleTimeoutSec,
    enabled: settings.enabled,
  });

  // Keep the display on while running as a fullscreen wall kiosk.
  useWakeLock(isFullscreen);

  // ----------------------------------------------------------------------
  // Homey-sensor wake — rising edge of any alarm_*/presence capability.
  // ----------------------------------------------------------------------
  // Tracks the previous truthiness of each watched capability so we only wake
  // on a false -> true transition, not on the steady-state or on falling edges.
  const prevValuesRef = useRef<Record<string, boolean>>({});
  // Whether we've recorded a baseline for the current sensor. The first run
  // after mount or a sensor change only seeds values; it never wakes (so a
  // sensor already reading "true" at load does not fire a spurious poke).
  const hasBaselineRef = useRef(false);

  const wakeDevice =
    settings.wakeSensorDeviceId !== null
      ? snapshot?.devices.find((d) => d.id === settings.wakeSensorDeviceId)
      : undefined;

  // Build a stable signature of the watched capability values so the effect
  // re-runs whenever the live snapshot patches a relevant value.
  const wakeSignature = wakeDevice
    ? Object.entries(wakeDevice.capabilitiesObj)
        .filter(([capId]) => isWakeCapability(capId))
        .map(([capId, cap]) => `${capId}:${String(cap.value)}`)
        .join("|")
    : "";

  // Reset tracking when the chosen sensor changes so a stale baseline from a
  // previous device can't suppress (or spuriously trigger) the first wake.
  useEffect(() => {
    prevValuesRef.current = {};
    hasBaselineRef.current = false;
  }, [settings.wakeSensorDeviceId]);

  useEffect(() => {
    if (!wakeDevice) return;

    let rising = false;
    const next: Record<string, boolean> = {};

    for (const [capId, cap] of Object.entries(wakeDevice.capabilitiesObj)) {
      if (!isWakeCapability(capId)) continue;
      const isTrue = cap.value === true;
      next[capId] = isTrue;
      const wasTrue = prevValuesRef.current[capId] ?? false;
      if (isTrue && !wasTrue) rising = true;
    }

    prevValuesRef.current = next;

    // First run only seeds the baseline; wake on rising edges thereafter.
    if (!hasBaselineRef.current) {
      hasBaselineRef.current = true;
      return;
    }
    if (rising) poke();
    // wakeSignature captures the watched values; poke is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wakeSignature]);

  // ----------------------------------------------------------------------
  // Camera wake — only while idle (screensaver up) and enabled in settings.
  // ----------------------------------------------------------------------
  useCameraMotion({
    enabled: settings.wakeOnCamera && isIdle,
    onMotion: poke,
  });

  return (
    <KioskScreensaver
      show={settings.enabled && isIdle}
      style={settings.screensaver}
      onWake={poke}
    />
  );
}

export default KioskController;
